import React, { useCallback, useContext, useEffect, useReducer } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { editFormForClientManagerReducer } from './editFormForClientManagerReducer';
import {
	EditFormForClientManagerContext,
	editFormForClientManagerInitialState,
	TTypeOfModalWindow,
} from './editFormForClientManagerContext';
import { EditFormForClientManagerActionTypes } from './EditFormForClientManagerActions';
import { IGetRestaurantInfo, IUpdateRestaurant, TMenuContentVersion } from 'types';
import { getRestaurantInfo } from 'backend/restaurants';
import { useNamedHeader } from 'hooks/useNamedHeader';
import { useFetchData } from 'hooks/useFetchData';

const EditFormForClientManagerState = ({ children }: JSX.ElementChildrenAttribute) => {
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const [state, dispatch] = useReducer(editFormForClientManagerReducer, editFormForClientManagerInitialState);

	const params = new URL(document.location.href).searchParams;
	const restaurantID = params.get('restaurantID');

	const { response, loading, refetch } = useFetchData<IGetRestaurantInfo, string>(
		restaurantID ? getRestaurantInfo : null,
		restaurantID || '',
	);

	useNamedHeader(response?.data.restaurant.name);

	useEffect(() => {
		if (state.needRefetchData) {
			refetch();
			dispatch({
				type: EditFormForClientManagerActionTypes.REFETCH_DATA,
				payload: {
					needRefetchData: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: EditFormForClientManagerActionTypes.SET_ALL_DATA,
				payload: {
					fullRestaurantInfo: response.data.restaurant,
					useMenuVersion: response.data.restaurant.useMenuVersion,
					useContentVersion: response.data.restaurant.useContentVersion,
					customerMenuVersion: response.data.restaurant.customerMenuVersion || '',
					customerContentVersion: response.data.restaurant.customerContentVersion || '',
					restaurantMenuVersion: response.data.restaurant.restaurantMenuVersion || '',
					restaurantContentVersion: response.data.restaurant.restaurantContentVersion || '',
				},
			});
			dispatch({
				type: EditFormForClientManagerActionTypes.SET_INITIAL_FORM_DATA,
				payload: {
					initialFormData: {
						useMenuVersion: response.data.restaurant.useMenuVersion,
						useContentVersion: response.data.restaurant.useContentVersion,
						restaurantMenuVersion: response.data.restaurant.restaurantMenuVersion || '',
						restaurantContentVersion: response.data.restaurant.restaurantContentVersion || '',
					},
				},
			});
		}
	}, [response]);

	const refetchData = useCallback((value: boolean) => {
		dispatch({
			type: EditFormForClientManagerActionTypes.REFETCH_DATA,
			payload: {
				needRefetchData: value,
			},
		});
	}, []);

	const toggleUseMenuVersion = useCallback(
		(value: TMenuContentVersion) => {
			dispatch({
				type: EditFormForClientManagerActionTypes.TOGGLE_USE_MENU_VERSION,
				payload: {
					useMenuVersion: value,
					restaurantMenuVersion:
						value === 'Customer' ? state.customerMenuVersion : state.restaurantMenuVersion,
				},
			});
		},
		[state.customerMenuVersion, state.restaurantMenuVersion],
	);

	const toggleUseContentVersion = useCallback(
		(value: TMenuContentVersion) => {
			dispatch({
				type: EditFormForClientManagerActionTypes.TOGGLE_USE_CONTENT_VERSION,
				payload: {
					useContentVersion: value,
					restaurantContentVersion:
						value === 'Customer' ? state.customerContentVersion : state.restaurantContentVersion,
				},
			});
		},
		[state.customerContentVersion, state.restaurantContentVersion],
	);

	const changeRestaurantMenuVersion = useCallback((value: string) => {
		dispatch({
			type: EditFormForClientManagerActionTypes.CHANGE_RESTAURANT_MENU_VERSION,
			payload: {
				restaurantMenuVersion: value,
			},
		});
	}, []);

	const changeRestaurantContentVersion = useCallback((value: string) => {
		dispatch({
			type: EditFormForClientManagerActionTypes.CHANGE_RESTAURANT_CONTENT_VERSION,
			payload: {
				restaurantContentVersion: value,
			},
		});
	}, []);

	const setDataForSubmit = useCallback((value: Nullable<IUpdateRestaurant>) => {
		dispatch({
			type: EditFormForClientManagerActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: EditFormForClientManagerActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const resetFields = useCallback(() => dispatch({ type: EditFormForClientManagerActionTypes.RESET_FIELDS }), []);

	return (
		<EditFormForClientManagerContext.Provider
			value={{
				refetchData,
				toggleUseMenuVersion,
				toggleUseContentVersion,
				changeRestaurantMenuVersion,
				changeRestaurantContentVersion,
				setDataForSubmit,
				changeTypeOfModalWindow,
				resetFields,
				fullRestaurantInfo: state.fullRestaurantInfo,
				needRefetchData: state.needRefetchData,
				initialFormData: state.initialFormData,
				useMenuVersion: state.useMenuVersion,
				useContentVersion: state.useContentVersion,
				customerMenuVersion: state.customerMenuVersion,
				customerContentVersion: state.customerContentVersion,
				restaurantMenuVersion: state.restaurantMenuVersion,
				restaurantContentVersion: state.restaurantContentVersion,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</EditFormForClientManagerContext.Provider>
	);
};

export default EditFormForClientManagerState;
